import { useState, type FormEvent } from "react";
import { Banner, Button, ConfirmDialog, Dialog, EmptyState, Field, Input, KeyValue, PageHeader, Select, Skeleton, StatusBadge, ThemeToggle, TonePill, apiFetch, useApi, useApiMutation, useCurrentUser, useLogout, useT, useToast } from "@iga/ui";
import type { Profile, Delegation, Person } from "../lib/types";
import { PeoplePicker, LoadError, PersonLine } from "../components/common";
import { TASKS_KEY, errMsg, useFmt } from "../lib/hooks";

function DelegationCard({ enabled }: { enabled: boolean }) {
  const t = useT();
  const fmt = useFmt();
  const me = useCurrentUser();
  const toast = useToast();
  const res = useApi<{ given: Delegation[]; received: Delegation[] }>(["delegations"], "/api/portal/me/delegations", undefined, { enabled });
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState<Person | null>(null);
  const [scope, setScope] = useState<"all" | "approvals" | "certifications">("all");
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [until, setUntil] = useState("");
  const [del, setDel] = useState<Delegation | null>(null);
  const keys = [["delegations"], TASKS_KEY, ["approvals"], ["certifications"]];
  const create = useApiMutation<void, { id: string }>(() => apiFetch("/api/portal/me/delegations", { method: "POST", json: { toIdentityId: to!.id, scope, validFrom: from ? new Date(from).toISOString() : undefined, validTo: until ? new Date(`${until}T23:59:59`).toISOString() : null } }), keys);
  const remove = useApiMutation<string, { ok: boolean }>((id) => apiFetch(`/api/portal/me/delegations/${id}`, { method: "DELETE" }), keys);
  const period = (d: Delegation) => `${fmt.day(d.validFrom)} – ${d.validTo ? fmt.day(d.validTo) : t("p.profile.untilRemoved")}`;
  const state = (d: Delegation) => d.active ? <TonePill tone="ok" compact>{t("p.profile.active")}</TonePill> : new Date(d.validFrom) > new Date() ? <TonePill tone="info" compact>{t("p.profile.scheduled")}</TonePill> : <TonePill tone="neutral" compact>{t("p.profile.ended")}</TonePill>;
  return (
    <section className="rounded-card border border-line bg-surface flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="text-h2">{t("p.profile.delegation")}</h2>{enabled && <Button size="touch" variant="secondary" onClick={() => setOpen(true)}>{t("p.profile.newDelegation")}</Button>}</div>
      <p className="text-fg-2">{t("p.profile.delegationBody")}</p>
      {!enabled ? null : res.isLoading ? <Skeleton rows={2} /> : res.error ? <LoadError error={res.error} /> : (
        <>
          <div>
            <h3 className="mb-1.5 text-label text-fg-3">{t("p.profile.iDelegateTo")}</h3>
            {res.data!.given.length === 0 ? <p className="text-fg-3">{t("p.profile.noDelegations")}</p> : (
              <ul className="divide-y divide-line">{res.data!.given.map((d) => (
                <li key={d.id} className="flex min-h-14 flex-wrap items-center justify-between gap-2 py-2">
                  <PersonLine person={d.to!} secondary={`${t(`p.profile.scope.${d.scope}`)} · ${period(d)}`} size={32} />
                  <span className="flex items-center gap-2">{state(d)}<Button size="touch" variant="ghost" onClick={() => setDel(d)}>{t("p.profile.remove")}</Button></span>
                </li>
              ))}</ul>
            )}
          </div>
          <div>
            <h3 className="mb-1.5 text-label text-fg-3">{t("p.profile.delegatedToMe")}</h3>
            {res.data!.received.length === 0 ? <p className="text-fg-3">{t("p.profile.nobodyDelegated")}</p> : (
              <ul className="divide-y divide-line">{res.data!.received.map((d) => (
                <li key={d.id} className="flex min-h-14 flex-wrap items-center justify-between gap-2 py-2"><PersonLine person={d.from!} secondary={`${t(`p.profile.scope.${d.scope}`)} · ${period(d)}`} size={32} />{state(d)}</li>
              ))}</ul>
            )}
          </div>
        </>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} size="sm" title={t("p.profile.newDelegation")} footer={<>
        <Button variant="secondary" onClick={() => setOpen(false)}>{t("ui.cancel")}</Button>
        <Button loading={create.isPending} disabled={!to} onClick={() => create.mutate(undefined, { onSuccess: () => { toast.push(t("p.profile.delegated", { name: to?.displayName ?? "" }), { tone: "ok" }); setOpen(false); setTo(null); setUntil(""); }, onError: (e) => toast.push(errMsg(e), { tone: "bad" }) })}>{t("p.profile.createDelegation")}</Button>
      </>}>
        <div className="flex flex-col gap-3">
          <Field label={t("p.profile.delegateTo")} htmlFor="deleg-to"><PeoplePicker id="deleg-to" value={to} onChange={setTo} exclude={me?.identity ? [me.identity.id] : []} autoFocus /></Field>
          <Field label={t("p.profile.scopeLabel")}><Select value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}><option value="all">{t("p.profile.scope.all")}</option><option value="approvals">{t("p.profile.scope.approvals")}</option><option value="certifications">{t("p.profile.scope.certifications")}</option></Select></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("p.profile.from")}><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
            <Field label={t("p.profile.until")} hint={t("p.profile.untilHint")}><Input type="date" value={until} min={from} onChange={(e) => setUntil(e.target.value)} /></Field>
          </div>
          <LoadError error={create.error} />
        </div>
      </Dialog>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} title={t("p.profile.removeTitle")} confirmLabel={t("p.profile.remove")} keepLabel={t("p.profile.keepDelegation")} loading={remove.isPending}
        consequence={t("p.profile.removeBody", { name: del?.to?.displayName ?? "" })}
        onConfirm={() => del && remove.mutate(del.id, { onSuccess: () => { toast.push(t("p.profile.removedToast"), { tone: "ok" }); setDel(null); }, onError: (e) => { toast.push(errMsg(e), { tone: "bad" }); setDel(null); } })} />
    </section>
  );
}

function ChangePasswordCard() {
  const t = useT();
  const me = useCurrentUser();
  const toast = useToast();
  const [current, setCurrent] = useState(""); const [next, setNext] = useState(""); const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null); const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (next !== confirm) { setError(t("p.profile.pwMismatch")); return; }
    setBusy(true); setError(null);
    try {
      await apiFetch("/api/auth/change-password", { method: "POST", json: { currentPassword: current || undefined, newPassword: next } });
      toast.push(t("p.profile.pwChanged"), { tone: "ok" }); setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  }
  return (
    <section className="rounded-card border border-line bg-surface flex flex-col gap-4 p-5">
      <h2 className="text-h2">{t("p.profile.changePassword")}</h2>
      {me?.user.mustChangePassword && <Banner tone="warn" title={t("p.profile.mustChange")} />}
      <form onSubmit={submit} className="flex flex-col gap-3">
        {!me?.user.mustChangePassword && <Field label={t("p.profile.currentPw")}><Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required /></Field>}
        <Field label={t("p.profile.newPw")} hint={t("p.profile.pwHint")}><Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={12} /></Field>
        <Field label={t("p.profile.confirmPw")} error={error}><Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => { setConfirm(e.target.value); setError(null); }} required invalid={!!error} /></Field>
        <div><Button type="submit" size="touch" variant="secondary" loading={busy}>{t("p.profile.changePassword")}</Button></div>
      </form>
    </section>
  );
}

export function ProfilePage() {
  const t = useT();
  const fmt = useFmt();
  const me = useCurrentUser();
  const logout = useLogout();
  const profile = useApi<Profile>(["me-profile"], "/api/portal/me/profile", undefined, { enabled: !!me?.identity });
  const p = profile.data;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader portal title={t("p.nav.profile")} subtitle={me?.user.email} actions={<Button variant="secondary" size="touch" onClick={() => void logout()}>{t("ui.signOut")}</Button>} />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section className="rounded-card border border-line bg-surface flex flex-col gap-4 p-5">
          <h2 className="text-h2">{t("p.profile.me")}</h2>
          {!me?.identity ? <p className="text-fg-2">{t("p.noIdentity.body")}</p>
            : profile.isLoading ? <Skeleton rows={3} variant="text" /> : profile.error ? <LoadError error={profile.error} /> : p ? (
              <KeyValue items={[
                { label: t("p.profile.name"), value: p.displayName }, { label: t("p.profile.email"), value: p.email && <span className="break-all">{p.email}</span> },
                { label: t("p.profile.userName"), value: p.userName }, { label: t("p.profile.employeeId"), value: p.externalId },
                { label: t("p.profile.type"), value: p.typeName }, { label: t("p.requests.col.status"), value: <StatusBadge domain="identity" status={p.status} audience="portal" params={{ date: fmt.day(p.validFrom) }} /> },
                { label: t("p.profile.jobTitle"), value: (p.attributes?.jobTitle as string | undefined) ?? null }, { label: t("p.profile.department"), value: (p.attributes?.department as string | undefined) ?? p.orgUnit?.name ?? null },
                { label: t("p.profile.manager"), value: p.manager ? <span>{p.manager.displayName}<span className="block break-all text-small text-fg-3">{p.manager.email}</span></span> : null }, { label: t("p.profile.orgUnit"), value: p.orgUnit?.name ?? null },
                { label: t("p.profile.directReports"), value: p.directReports }, { label: t("p.profile.validTo"), value: p.validTo ? fmt.day(p.validTo) : t("p.access.noEndDate") },
              ]} />
            ) : <EmptyState title={t("p.profile.none")} />}
          <div className="flex flex-col gap-1.5 border-t border-line pt-3">
            <span className="text-label text-fg-3">{t("ui.theme")}</span>
            <ThemeToggle size="touch" className="self-start" />
          </div>
        </section>
        <div className="flex flex-col gap-5">
          <DelegationCard enabled={!!me?.identity} />
          <ChangePasswordCard />
        </div>
      </div>
    </div>
  );
}
